import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import Typography from "@mui/material/Typography"
import MoneyIcon from '@mui/icons-material/Money';
import MoreVert from '@mui/icons-material/MoreVert';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { ProfitSummary } from "../../interface/ProfitSummary";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useEffect, useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { alpha, useTheme } from "@mui/material/styles";
import { dashboardMutedTextSx, getDashboardPanelSx } from "./dashboardStyles";

interface ProfitCardProps
{
    loading: boolean
    profitSummary: ProfitSummary[] | undefined
}

const KEY_PROFIT_TIMEFRAME_INDEX = "KEY_PROFIT_TIMEFRAME_INDEX"

export const ProfitCard = ({ loading, profitSummary }: ProfitCardProps) =>
{
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [ item, setItem ] = useState<ProfitSummary | undefined>(undefined) 

  const handleSelect = (item: ProfitSummary, index: number) => {
    setAnchorEl(null)
    setItem(item)
    localStorage.setItem(KEY_PROFIT_TIMEFRAME_INDEX, String(index))
  }

  useEffect(() => {

    if(profitSummary)
    {
      const indexValue = localStorage.getItem(KEY_PROFIT_TIMEFRAME_INDEX)
      setItem(profitSummary[Number(indexValue) || 0])
    }

  }, [profitSummary])

  return (
    <Card sx={{ ...getDashboardPanelSx(theme, 250), p: 3 }} elevation={0}>
    
    { loading ? <>

      <Skeleton height={30} width={30} variant="circular" /> 
      <Skeleton height={20} width="50%" sx={{ mt: 1 }} /> 
      <Skeleton height={52} width="80%" sx={{ mt: 1 }} /> 
      <Skeleton height={18} width="45%" /> 
      <Skeleton height={92} width="100%" sx={{ mt: 1 }} /> 

    </>
    : <>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ position: "relative", zIndex: 1 }}>
      <Box>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "10px",
            display: "grid",
            placeItems: "center",
            color: "#7cf1b8",
            backgroundColor: alpha("#7cf1b8", theme.palette.mode === "dark" ? 0.14 : 0.2),
            mb: 1.5,
          }}
        >
          <MoneyIcon fontSize="large" />
        </Box>
        <Typography variant="overline" sx={{ letterSpacing: 1.6, ...dashboardMutedTextSx(theme) }}>
          Profit Snapshot
        </Typography>
      </Box>

      <Button
        size="small"
        variant="outlined"
        sx={{
          borderColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.12 : 0.18),
          color: "text.primary",
          backdropFilter: "blur(8px)",
        }}
        endIcon={<MoreVert />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {item?.time}
      </Button>
    </Stack>

    <Box sx={{ position: "relative", zIndex: 1, mt: 2 }}>
      <Typography sx={{ fontSize: { xs: 36, md: 42 }, lineHeight: 1, fontWeight: 700 }}>
        ${item?.profit || 0}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 17, ...dashboardMutedTextSx(theme) }}>
        Realised profit in the selected timeframe
      </Typography>
      <Stack direction="row" spacing={1.25} sx={{ mt: 2.5, flexWrap: "wrap" }}>
        <Chip
          icon={<TrendingUpRoundedIcon />}
          label={item?.time || "Current"}
          sx={{
            borderRadius: 999,
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.12),
            color: "text.primary",
          }}
        />
        <Chip
          label={`${profitSummary?.length || 0} timeframes`}
          sx={{
            borderRadius: 999,
            backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6),
            color: "text.primary",
          }}
        />
      </Stack>
    </Box>

    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        mt: 3,
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.12)}`,
        backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.55),
      }}
    >
      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>
        Switch the timeframe from the control above to compare short-term and long-term performance.
      </Typography>
    </Box>

    <div style={{ position: "absolute", top: 16, right: 16 }}>
       <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        { !loading && profitSummary?.map((item, key) => <MenuItem onClick={() => handleSelect(item, key)} key={key} value={item.profit}>{item.time}</MenuItem>)}
      </Menu>
    </div>

    </>}
  </Card>
  )
}
